import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Pagination,
  Skeleton,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import VerifiedIcon from "@mui/icons-material/Verified";
import GppMaybeOutlinedIcon from "@mui/icons-material/GppMaybeOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import StarIcon from "@mui/icons-material/Star";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ReadMoreIcon from "@mui/icons-material/ReadMore";
import PageContainer from "../../Shared/PageContainer/PageContainer";
import { adminFromLocalStg } from "../../../helpers/AdminFromLocalStorage/AdminFromLocalStorage";
import {
  GET_CMZ_LIBRARY_QUOTES,
  GET_SCHEDULED_QOTD,
} from "../../../graphql/IslamicQuotes/query";
import { UPDATE_CMZ_LIBRARY_QUOTE } from "../../../graphql/IslamicQuotes/mutation";
import {
  CATEGORY_LABELS,
  IslamicQuote,
  IslamicQuoteFilterInput,
  QuoteOfTheDaySchedule,
} from "../../../graphql/IslamicQuotes/types";
import QuoteFormDialog from "./QuoteFormDialog";
import DeleteConfirmDialog from "./DeleteConfirmDialog";
import ScheduleQotdDialog from "./ScheduleQotdDialog";
import QotdScheduleManager from "./QotdScheduleManager";
import ViewQuoteDialog from "./ViewQuoteDialog";
import { useVerseText } from "./quran";
import "./QuranVerses.css";

const PAGE_LIMIT = 12;

// Local-time YYYY-MM-DD (not UTC).
const toYmd = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// "3 Sep" style short label from a YYYY-MM-DD string (parsed as local).
const prettyShortDate = (ymd: string): string => {
  const [y, m, d] = ymd.split("-").map(Number);
  if (!y || !m || !d) return ymd;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
  });
};

const QuranVerses: React.FC = () => {
  const admin = adminFromLocalStg("admin");
  const isSuperAdmin = admin?.role === "superadmin";

  // ── Filter state ────────────────────────────────────────────────────────────
  const [tag, setTag] = useState("");
  const [label, setLabel] = useState("");
  // searchInput is the live text field; searchTerm is what's actually queried
  //   (committed on Enter / Search click) so we don't hammer the API per keypress.
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);

  // ── Dialog state ────────────────────────────────────────────────────────────
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<IslamicQuote | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<IslamicQuote | null>(null);
  const [scheduleTarget, setScheduleTarget] = useState<IslamicQuote | null>(null);
  const [viewTarget, setViewTarget] = useState<IslamicQuote | null>(null);
  const [qotdManagerOpen, setQotdManagerOpen] = useState(false);

  // Single toast used for lightweight success/error feedback.
  const [notice, setNotice] = useState<{
    msg: string;
    severity: "success" | "error";
  } | null>(null);

  const filter: IslamicQuoteFilterInput = useMemo(
    () => ({
      ...(tag.trim() ? { tag: tag.trim() } : {}),
      ...(label.trim() ? { label: label.trim() } : {}),
      ...(searchTerm.trim() ? { searchTerm: searchTerm.trim() } : {}),
      page,
      limit: PAGE_LIMIT,
    }),
    [tag, label, searchTerm, page]
  );

  const { data, loading, error, refetch } = useQuery(GET_CMZ_LIBRARY_QUOTES, {
    variables: { filter },
    fetchPolicy: "cache-and-network",
    skip: !isSuperAdmin,
  });

  const quotes: IslamicQuote[] = data?.getCMZLibraryQuotes?.quotes || [];
  const totalCount: number = data?.getCMZLibraryQuotes?.totalCount || 0;
  const pageCount = Math.max(1, Math.ceil(totalCount / PAGE_LIMIT));

  // Page-level view of upcoming QOTD schedules (today → +1y) so every library
  //   card can show whether — and for which dates — it is scheduled. Shares the
  //   "GetScheduledQuotesOfTheDay" operation name, so schedule/unschedule
  //   mutations elsewhere refetch this automatically.
  const scheduleRange = useMemo(() => {
    const today = new Date();
    const end = new Date();
    end.setDate(end.getDate() + 365);
    return { startDate: toYmd(today), endDate: toYmd(end) };
  }, []);
  const { data: schedData, refetch: refetchSchedules } = useQuery(
    GET_SCHEDULED_QOTD,
    {
      variables: scheduleRange,
      skip: !isSuperAdmin,
      fetchPolicy: "cache-and-network",
    }
  );
  const schedules: QuoteOfTheDaySchedule[] =
    schedData?.getScheduledQuotesOfTheDay || [];
  const upcomingCount = schedules.length;

  // quoteId → sorted list of scheduled dates.
  const scheduledByQuote = useMemo(() => {
    const map: Record<string, string[]> = {};
    schedules.forEach((s) => {
      (map[s.quoteId] ||= []).push(s.date);
    });
    Object.values(map).forEach((d) => d.sort());
    return map;
  }, [schedules]);

  const [updateQuote] = useMutation(UPDATE_CMZ_LIBRARY_QUOTE);
  // id currently being toggled → disables its buttons to prevent double-clicks.
  const [togglingId, setTogglingId] = useState<string | null>(null);

  // Keep the current page within range: deleting/deactivating the last item on
  //   the last page (or tightening a filter) can shrink pageCount below `page`.
  // Skip while loading — cache-and-network briefly yields empty data (totalCount 0
  // → pageCount 1), which would otherwise snap page 2+ back to 1 mid-fetch.
  useEffect(() => {
    if (loading) return;
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount, loading]);

  const handleToggle = useCallback(
    async (q: IslamicQuote, field: "isVerified" | "isActive") => {
      setTogglingId(q.id);
      try {
        await updateQuote({
          variables: { input: { id: q.id, [field]: !q[field] } },
        });
        await refetch();
      } catch (err: any) {
        setNotice({
          severity: "error",
          msg:
            err?.graphQLErrors?.[0]?.message ||
            err?.message ||
            "Failed to update the quote. Please try again.",
        });
      } finally {
        setTogglingId(null);
      }
    },
    [updateQuote, refetch]
  );

  const handleScheduleClose = useCallback(
    (didSchedule: boolean) => {
      setScheduleTarget(null);
      if (didSchedule) {
        // Explicit refetch (in addition to the mutation's refetchQueries) so the
        //   card reliably flips to its scheduled pill right after scheduling.
        refetchSchedules();
        setNotice({
          severity: "success",
          msg: "Quote of the Day scheduled and pushed to all connected TVs.",
        });
      }
    },
    [refetchSchedules]
  );

  // ── Handlers ────────────────────────────────────────────────────────────────
  const commitSearch = () => {
    setSearchTerm(searchInput);
    setPage(1);
  };

  const clearFilters = () => {
    setTag("");
    setLabel("");
    setSearchInput("");
    setSearchTerm("");
    setPage(1);
  };

  const hasActiveFilters =
    !!tag.trim() || !!label.trim() || !!searchTerm.trim();

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (q: IslamicQuote) => {
    setEditing(q);
    setFormOpen(true);
  };

  const handleFormClose = useCallback(
    (didSave: boolean) => {
      setFormOpen(false);
      setEditing(null);
      if (didSave) refetch();
    },
    [refetch]
  );

  const handleDeleteClose = useCallback(
    (didDelete: boolean) => {
      setDeleteTarget(null);
      if (didDelete) refetch();
    },
    [refetch]
  );

  // ── Super-admin gate ──────────────────────────────────────────────────────────
  if (!isSuperAdmin) {
    return (
      <PageContainer title="Islamic Quotes Library">
        <Box sx={{ p: 4 }}>
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            The Islamic Quotes Library is managed by Super Admins only. You do
            not have access to curate library content.
          </Alert>
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer title="Islamic Quotes Library">
      <div className="QV_container">
        {/* ══ Header ══════════════════════════════════════════════════════════ */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1.5}
        >
          <Stack direction="row" alignItems="center" gap={1}>
            <MenuBookIcon sx={{ color: "#6366f1" }} />
            <Box>
              <Typography variant="subtitle1" fontWeight={700} color="#1e1b4b">
                CMZ Quotes Library
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Global, pre-verified quotes shown across all mosques.
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" gap={1.5} flexWrap="wrap">
            <Badge badgeContent={upcomingCount} color="primary" max={99}>
              <Button
                variant="outlined"
                startIcon={<StarIcon />}
                onClick={() => setQotdManagerOpen(true)}
                sx={{ borderRadius: 2, whiteSpace: "nowrap" }}
              >
                Scheduled QOTD
              </Button>
            </Badge>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreate}
              sx={{ borderRadius: 2, whiteSpace: "nowrap" }}
            >
              Add Quote
            </Button>
          </Stack>
        </Stack>

        <Divider />

        {/* ══ Filter bar ══════════════════════════════════════════════════════ */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          gap={1.5}
          alignItems={{ xs: "stretch", md: "center" }}
          flexWrap="wrap"
        >
          <TextField
            size="small"
            label="Search"
            placeholder="Text or reference…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && commitSearch()}
            sx={{ minWidth: 220, flex: 1 }}
            InputProps={{
              endAdornment: (
                <IconButton size="small" onClick={commitSearch}>
                  <SearchIcon fontSize="small" />
                </IconButton>
              ),
            }}
          />

          <TextField
            size="small"
            label="Tag"
            value={tag}
            onChange={(e) => {
              setTag(e.target.value);
              setPage(1);
            }}
            sx={{ minWidth: 140 }}
          />

          <TextField
            size="small"
            label="Label"
            value={label}
            onChange={(e) => {
              setLabel(e.target.value);
              setPage(1);
            }}
            sx={{ minWidth: 140 }}
          />

          {hasActiveFilters && (
            <Button
              size="small"
              color="inherit"
              startIcon={<ClearIcon />}
              onClick={clearFilters}
              sx={{ whiteSpace: "nowrap" }}
            >
              Clear
            </Button>
          )}
        </Stack>

        {/* Result count */}
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="body2" color="text.secondary">
            {loading ? "Loading…" : `${totalCount} quote${totalCount === 1 ? "" : "s"}`}
          </Typography>
          {loading && <CircularProgress size={14} />}
        </Stack>

        {/* ══ Error ═══════════════════════════════════════════════════════════ */}
        {error && (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {error.message || "Failed to load quotes."}
          </Alert>
        )}

        {/* ══ Loading skeletons ═══════════════════════════════════════════════ */}
        {loading && quotes.length === 0 && (
          <div className="QV_libList">
            {[0, 1, 2].map((i) => (
              <div className="QV_skeletonCard" key={i}>
                <Skeleton variant="text" width="30%" height={20} />
                <Skeleton variant="text" width="70%" height={34} sx={{ mx: "auto" }} />
                <Skeleton variant="text" width="55%" height={20} sx={{ mx: "auto" }} />
                <Skeleton variant="text" width="35%" height={16} />
              </div>
            ))}
          </div>
        )}

        {/* ══ Empty state ═════════════════════════════════════════════════════ */}
        {!loading && quotes.length === 0 && !error && (
          <div className="QV_emptyState">
            <MenuBookIcon sx={{ fontSize: 40, color: "#d1d5db" }} />
            <Typography variant="body2" color="text.disabled">
              {hasActiveFilters
                ? "No quotes match your filters."
                : "No library quotes yet. Add the first one."}
            </Typography>
          </div>
        )}

        {/* ══ Quote list ══════════════════════════════════════════════════════ */}
        {quotes.length > 0 && (
          <div className="QV_libList">
            {quotes.map((q) => (
              <QuoteRow
                key={q.id}
                quote={q}
                busy={togglingId === q.id}
                scheduledDates={scheduledByQuote[q.id] || []}
                onView={() => setViewTarget(q)}
                onEdit={() => openEdit(q)}
                onDelete={() => setDeleteTarget(q)}
                onToggle={(field) => handleToggle(q, field)}
                onSchedule={() => setScheduleTarget(q)}
                onManageSchedule={() => setQotdManagerOpen(true)}
              />
            ))}
          </div>
        )}

        {/* ══ Pagination ══════════════════════════════════════════════════════ */}
        {pageCount > 1 && (
          <Stack alignItems="center" mt={1}>
            <Pagination
              count={pageCount}
              page={page}
              onChange={(_, p) => setPage(p)}
              color="primary"
              shape="rounded"
            />
          </Stack>
        )}
      </div>

      {/* ══ Create / Edit dialog ══════════════════════════════════════════════ */}
      <QuoteFormDialog
        open={formOpen}
        quote={editing}
        onClose={handleFormClose}
      />

      {/* ══ Delete confirm dialog ═════════════════════════════════════════════ */}
      <DeleteConfirmDialog
        quote={deleteTarget}
        onClose={handleDeleteClose}
      />

      {/* ══ View full quote (read-only) ═══════════════════════════════════════ */}
      <ViewQuoteDialog
        quote={viewTarget}
        onClose={() => setViewTarget(null)}
        onEdit={() => {
          const q = viewTarget;
          setViewTarget(null);
          if (q) openEdit(q);
        }}
      />

      {/* ══ Schedule as Quote of the Day ══════════════════════════════════════ */}
      <ScheduleQotdDialog
        quote={scheduleTarget}
        onClose={handleScheduleClose}
      />

      {/* ══ Quote of the Day manager (list + unschedule) ══════════════════════ */}
      <QotdScheduleManager
        open={qotdManagerOpen}
        onClose={() => setQotdManagerOpen(false)}
        onChanged={() => {
          refetchSchedules();
          setNotice({
            severity: "success",
            msg: "Schedule updated and pushed to all connected TVs.",
          });
        }}
      />

      {/* ══ Toast feedback (success / error) ══════════════════════════════════ */}
      <Snackbar
        open={!!notice}
        autoHideDuration={5000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={notice?.severity || "info"}
          variant="filled"
          onClose={() => setNotice(null)}
          sx={{ borderRadius: 2 }}
        >
          {notice?.msg}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

// ─── Quote row ─────────────────────────────────────────────────────────────────
type QuoteRowProps = {
  quote: IslamicQuote;
  busy: boolean;
  scheduledDates: string[];
  onView: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onToggle: (field: "isVerified" | "isActive") => void;
  onSchedule: () => void;
  onManageSchedule: () => void;
};

const QuoteRow: React.FC<QuoteRowProps> = ({
  quote,
  busy,
  scheduledDates,
  onView,
  onEdit,
  onDelete,
  onToggle,
  onSchedule,
  onManageSchedule,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchor);
  const closeMenu = () => setMenuAnchor(null);
  const run = (fn: () => void) => () => {
    closeMenu();
    fn();
  };

  // QURAN quotes carry no stored text — resolve Arabic + a preview translation
  //   via cmz-backend Quran proxy by surah/ayah. Other categories use their stored text.
  const isQuranRef = quote.category === "QURAN" && !quote.arabicText;
  const { data: verse, loading: verseLoading } = useVerseText(
    isQuranRef ? quote.surahNumber : null,
    isQuranRef ? quote.ayahNumber : null
  );
  const displayArabic = quote.arabicText || verse?.arabic || "";
  const displayTranslation = quote.translation || verse?.english || "";

  // Detect whether the clamped Arabic / translation actually overflow, so the
  //   "View full" button only shows on cards whose text is truncated.
  const arabicRef = useRef<HTMLDivElement>(null);
  const translationRef = useRef<HTMLDivElement>(null);
  const [truncated, setTruncated] = useState(false);

  useEffect(() => {
    const overflows = (el: HTMLElement | null) =>
      !!el && el.scrollHeight - el.clientHeight > 2;
    const check = () =>
      setTruncated(
        overflows(arabicRef.current) || overflows(translationRef.current)
      );
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [displayArabic, displayTranslation]);

  const isScheduled = scheduledDates.length > 0;
  const scheduledLabel = isScheduled
    ? `Quote of the Day · ${prettyShortDate(scheduledDates[0])}${
        scheduledDates.length > 1 ? ` +${scheduledDates.length - 1} more` : ""
      }`
    : "";
  const scheduledTitle = isScheduled
    ? `Scheduled for ${scheduledDates
        .map(prettyShortDate)
        .join(", ")} — click to manage`
    : "";

  return (
    <div
      className={`QV_row${quote.isActive ? "" : " QV_row--inactive"}${
        isScheduled ? " QV_row--scheduled" : ""
      }`}
    >
      {/* Top meta line: category + status (left), actions (right) */}
      <div className="QV_rowTop">
        <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap">
          <Chip
            label={CATEGORY_LABELS[quote.category] || quote.category}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 600, fontSize: 11, height: 22 }}
          />
          {quote.isVerified ? (
            <Stack direction="row" alignItems="center" gap={0.25}>
              <VerifiedIcon sx={{ fontSize: 15, color: "#16a34a" }} />
              <Typography variant="caption" sx={{ color: "#16a34a", fontWeight: 600 }}>
                Verified
              </Typography>
            </Stack>
          ) : (
            <Stack direction="row" alignItems="center" gap={0.25}>
              <GppMaybeOutlinedIcon sx={{ fontSize: 15, color: "#d97706" }} />
              <Typography variant="caption" sx={{ color: "#d97706", fontWeight: 600 }}>
                Unverified
              </Typography>
            </Stack>
          )}
          {!quote.isActive && (
            <Chip
              label="Inactive"
              size="small"
              sx={{ fontSize: 10, height: 20, bgcolor: "#f3f4f6", color: "#6b7280" }}
            />
          )}
        </Stack>

        <Stack direction="row" alignItems="center" gap={0.75}>
          {isScheduled ? (
            <Tooltip title={scheduledTitle}>
              <Chip
                icon={<StarIcon sx={{ fontSize: 15 }} />}
                label={scheduledLabel}
                size="small"
                onClick={onManageSchedule}
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  height: 24,
                  bgcolor: "#fef3c7",
                  color: "#92400e",
                  cursor: "pointer",
                  "& .MuiChip-icon": { color: "#d97706" },
                }}
              />
            </Tooltip>
          ) : (
            <Tooltip
              title={
                quote.isActive
                  ? ""
                  : "Activate this quote before scheduling"
              }
            >
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  startIcon={<EventAvailableIcon />}
                  disabled={!quote.isActive}
                  onClick={onSchedule}
                  sx={{ borderRadius: 2, whiteSpace: "nowrap", height: 30 }}
                >
                  Schedule
                </Button>
              </span>
            </Tooltip>
          )}

          <Tooltip title="More actions">
            <IconButton
              size="small"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={menuAnchor}
            open={menuOpen}
            onClose={closeMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            {isScheduled && (
              <MenuItem onClick={run(onSchedule)} disabled={!quote.isActive}>
                <ListItemIcon>
                  <EventAvailableIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Schedule another date</ListItemText>
              </MenuItem>
            )}
            <MenuItem onClick={run(onEdit)}>
              <ListItemIcon>
                <EditOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </MenuItem>
            <MenuItem disabled={busy} onClick={run(() => onToggle("isVerified"))}>
              <ListItemIcon>
                {quote.isVerified ? (
                  <GppMaybeOutlinedIcon fontSize="small" />
                ) : (
                  <VerifiedIcon fontSize="small" sx={{ color: "#16a34a" }} />
                )}
              </ListItemIcon>
              <ListItemText>
                {quote.isVerified ? "Mark unverified" : "Mark verified"}
              </ListItemText>
            </MenuItem>
            <MenuItem disabled={busy} onClick={run(() => onToggle("isActive"))}>
              <ListItemIcon>
                {quote.isActive ? (
                  <VisibilityOffIcon fontSize="small" />
                ) : (
                  <VisibilityIcon fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText>
                {quote.isActive ? "Deactivate" : "Activate"}
              </ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={run(onDelete)} sx={{ color: "error.main" }}>
              <ListItemIcon>
                <DeleteOutlineIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>
          </Menu>
        </Stack>
      </div>

      {/* Quote body — centered vertically so short quotes stay balanced when
          the row is stretched to a taller sibling's height. */}
      <div className="QV_rowBody">
        {isQuranRef && verseLoading && !displayArabic ? (
          <>
            <Skeleton variant="text" width="70%" height={34} sx={{ mx: "auto" }} />
            <Skeleton variant="text" width="55%" height={22} sx={{ mx: "auto" }} />
          </>
        ) : (
          <>
            <div
              ref={arabicRef}
              className="QV_arabic QV_rowArabic"
              title={displayArabic}
            >
              {displayArabic}
            </div>

            {displayTranslation && (
              <div
                ref={translationRef}
                className="QV_rowTranslation"
                title={displayTranslation}
              >
                {displayTranslation}
              </div>
            )}
          </>
        )}

        {quote.transliteration && (
          <Typography
            variant="caption"
            color="text.secondary"
            fontStyle="italic"
            sx={{ display: "block", textAlign: "center" }}
          >
            {quote.transliteration}
          </Typography>
        )}

        {truncated && (
          <Button
            size="small"
            startIcon={<ReadMoreIcon />}
            onClick={onView}
            sx={{
              alignSelf: "center",
              textTransform: "none",
              borderRadius: 2,
              mt: 0.5,
            }}
          >
            View full
          </Button>
        )}
      </div>

      {/* Footer: reference + tags/labels */}
      <div className="QV_rowFooter">
        <Typography
          variant="caption"
          sx={{ color: "#6366f1", fontWeight: 700, whiteSpace: "nowrap" }}
        >
          {quote.reference}
        </Typography>
        {(quote.tags.length > 0 || quote.labels.length > 0) && (
          <div className="QV_rowChips">
            {quote.tags.map((t) => (
              <Chip
                key={`t-${t}`}
                label={t}
                size="small"
                variant="outlined"
                sx={{ fontSize: 10, height: 20 }}
              />
            ))}
            {quote.labels.map((l) => (
              <Chip
                key={`l-${l}`}
                label={l}
                size="small"
                sx={{ fontSize: 10, height: 20, bgcolor: "#eef2ff", color: "#4338ca" }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuranVerses;
